import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  PhoneCall,
  ShieldAlert,
  Stethoscope,
} from "lucide-react";
import { api, ApiError, okSchema } from "@/lib/api";
import { PageHeader } from "@/components/layout/AppShell";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Input, Select } from "@/components/ui/Field";
import { Alert, UrgencyPanel } from "@/components/ui/Feedback";
import { useToast } from "@/components/ui/toast";
import { cn } from "@/lib/cn";
import {
  CHECK_PROGRAMS,
  METRICS,
  RED_FLAGS,
  SEVERITIES,
  SYMPTOMS,
  type CheckType,
  type MetricKey,
} from "./healthCheckConfig";

const startSchema = z.looseObject({
  session_uuid: z.string(),
  status: z.string().default("collecting"),
});

const completeSchema = z.looseObject({
  session_uuid: z.string(),
  urgency: z.string(),
});

type Step = "consent" | "danger" | "vitals" | "symptoms" | "submitting";

const STEP_LABELS: Record<Exclude<Step, "submitting">, string> = {
  consent: "Persetujuan",
  danger: "Tanda bahaya",
  vitals: "Tanda vital",
  symptoms: "Gejala",
};

export function HealthCheckWizard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [step, setStep] = useState<Step>("consent");
  const [consent, setConsent] = useState(false);
  const [program, setProgram] = useState<CheckType>("quick");
  const [sessionUuid, setSessionUuid] = useState<string | null>(null);

  const [redFlags, setRedFlags] = useState<Set<string>>(new Set());
  /** Set once the emergency has been recorded; the session is closed after
   *  that, so the check can no longer be resumed. */
  const [escalated, setEscalated] = useState(false);
  const [values, setValues] = useState<Partial<Record<MetricKey, string>>>({});
  const [errors, setErrors] = useState<Partial<Record<MetricKey, string>>>({});
  const [symptoms, setSymptoms] = useState<Map<string, string>>(new Map());

  const [busy, setBusy] = useState(false);
  const [fatal, setFatal] = useState<string | null>(null);

  const metrics = CHECK_PROGRAMS.find((item) => item.value === program)?.metrics ?? [];
  const emergency = redFlags.size > 0;

  // ---- step 1: consent -> create the session ----------------------------
  async function startSession() {
    setBusy(true);
    setFatal(null);
    try {
      const data = await api.post("/api/health-check/start", startSchema, {
        check_type: program,
        consent: true,
        source: "spa",
      });
      setSessionUuid(data.session_uuid);
      setStep("danger");
    } catch (cause) {
      setFatal(cause instanceof ApiError ? cause.message : "Tidak dapat memulai pemeriksaan.");
    } finally {
      setBusy(false);
    }
  }

  // ---- step 2: danger signs --------------------------------------------
  /**
   * Records the red flags as `severe` symptoms and closes the session. The
   * wizard does not continue to measurements: nothing a patient could enter
   * afterwards should soften an emergency, and asking them to keep typing
   * while they need an ambulance is the wrong instruction.
   *
   * Deliberately not called from the checkbox. Completing the session is
   * irreversible, so doing it on tick meant a patient who ticked a box by
   * accident could never finish the check — every later step answered 404 and
   * the wizard showed "Terjadi kesalahan pada server", while the panel's own
   * "Saya salah tandai" button offered a recovery that could not work.
   *
   * `flags` is passed in rather than read from state: this used to run in the
   * same tick as setRedFlags, so it saw the set from *before* the box was
   * ticked and logged nothing at all.
   */
  async function escalate(flags: Set<string>) {
    if (!sessionUuid || escalated) return;
    setEscalated(true);
    setBusy(true);
    try {
      for (const code of flags) {
        const flag = RED_FLAGS.find((item) => item.code === code);
        await api.post(`/api/health-check/${sessionUuid}/symptom`, okSchema, {
          symptom_code: code,
          symptom_name: flag?.name ?? code,
          severity: "severe",
          answer_boolean: true,
        });
      }
      await api.post(`/api/health-check/${sessionUuid}/complete`, completeSchema, {});
    } catch {
      // The emergency panel is already on screen; a failed log must not
      // replace the instruction to call 119.
    } finally {
      setBusy(false);
      void queryClient.invalidateQueries({ queryKey: ["patient"] });
    }
  }

  // ---- step 3: vitals ---------------------------------------------------
  function validate(): boolean {
    const next: Partial<Record<MetricKey, string>> = {};
    let filled = 0;

    for (const key of metrics) {
      const raw = values[key]?.trim();
      if (!raw) continue;
      const numeric = Number(raw);
      const [low, high] = METRICS[key].hard;
      if (!Number.isFinite(numeric)) {
        next[key] = "Masukkan angka yang valid.";
      } else if (numeric < low || numeric > high) {
        // Mirrors the server's hard bounds so the patient is told before the
        // round trip, not after.
        next[key] = `Nilai harus antara ${low} dan ${high} ${METRICS[key].unit}.`;
      } else {
        filled += 1;
      }
    }

    setErrors(next);
    if (Object.keys(next).length > 0) return false;
    if (filled === 0) {
      toast.warning({
        title: "Belum ada pengukuran",
        description: "Isi minimal satu tanda vital untuk dianalisis.",
      });
      return false;
    }
    return true;
  }

  async function submitVitals() {
    if (!sessionUuid || !validate()) return;
    setBusy(true);
    const rejected: string[] = [];

    try {
      for (const key of metrics) {
        const raw = values[key]?.trim();
        if (!raw) continue;
        try {
          await api.post(`/api/health-check/${sessionUuid}/measurement`, okSchema, {
            metric_type: key,
            value: Number(raw),
            source_type: "manual",
            // The server asks for a second reading on implausible values; the
            // patient has already entered it once here, so confirm explicitly.
            confirmed_extreme: true,
          });
        } catch (cause) {
          rejected.push(
            `${METRICS[key].label}: ${cause instanceof ApiError ? cause.message : "gagal disimpan"}`,
          );
        }
      }

      if (rejected.length > 0) {
        toast.warning({
          title: "Sebagian pengukuran ditolak",
          description: rejected.join(" · "),
        });
      }
      setStep("symptoms");
    } finally {
      setBusy(false);
    }
  }

  // ---- step 4: symptoms -> complete -------------------------------------
  async function finish() {
    if (!sessionUuid) return;
    setBusy(true);
    setStep("submitting");
    try {
      for (const [code, severity] of symptoms) {
        const symptom = SYMPTOMS.find((item) => item.code === code);
        await api.post(`/api/health-check/${sessionUuid}/symptom`, okSchema, {
          symptom_code: code,
          symptom_name: symptom?.name ?? code,
          severity,
          answer_boolean: true,
        });
      }
      const result = await api.post(
        `/api/health-check/${sessionUuid}/complete`,
        completeSchema,
        {},
      );
      void queryClient.invalidateQueries({ queryKey: ["patient"] });
      navigate(`/patient/health-check/result/${result.session_uuid}`, { replace: true });
    } catch (cause) {
      setStep("symptoms");
      // "invalid_session" means this check can no longer be finished, so the
      // patient needs to start a new one — telling them the server erred
      // invites them to retry something that will never succeed.
      setFatal(
        cause instanceof ApiError && cause.code === "invalid_session"
          ? "Sesi pemeriksaan ini sudah ditutup. Mulai pemeriksaan baru untuk melanjutkan."
          : cause instanceof ApiError
            ? cause.message
            : "Analisis gagal. Coba lagi sebentar lagi.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader
        eyebrow="Pemeriksaan mandiri"
        title="Cek Kesehatan AI"
        description="Alat screening awal. Bukan pengganti pemeriksaan tenaga medis."
      />

      {step !== "submitting" && <StepIndicator current={step} />}

      {fatal && (
        <Alert tone="danger" title="Gagal" className="mb-4">
          {fatal}
        </Alert>
      )}

      {step === "consent" && (
        <Card>
          <CardHeader
            eyebrow="Langkah 1"
            title="Persetujuan dan program"
            description="Pilih jenis pemeriksaan, lalu berikan persetujuan pemrosesan data."
          />
          <CardBody className="space-y-5">
            <div className="grid gap-2.5 sm:grid-cols-2">
              {CHECK_PROGRAMS.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => setProgram(item.value)}
                  aria-pressed={program === item.value}
                  className={cn(
                    "rounded-xl border px-4 py-3 text-left transition-colors",
                    program === item.value
                      ? "border-accent bg-accent/10"
                      : "border-hairline bg-surface hover:border-hairline-strong",
                  )}
                >
                  <span className="block text-sm font-bold">{item.label}</span>
                  <span className="text-content-muted mt-0.5 block text-xs">
                    {item.description}
                  </span>
                  <span className="text-content-subtle mt-1.5 block text-2xs">
                    {item.metrics.length} pengukuran
                  </span>
                </button>
              ))}
            </div>

            <Alert tone="info" title="Apa yang terjadi dengan data Anda">
              Hasil pengukuran disimpan pada rekam kesehatan Anda dan dapat dilihat tim medis RS
              Mediora. Alert dengan urgensi tinggi diteruskan ke pusat monitoring agar dapat
              ditindaklanjuti.
            </Alert>

            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                checked={consent}
                onChange={(event) => setConsent(event.target.checked)}
                className="accent-accent mt-0.5 size-4 shrink-0"
              />
              <span className="text-sm">
                Saya menyetujui pemrosesan data kesehatan saya untuk keperluan screening dan
                monitoring, dan memahami bahwa hasil ini <b>bukan diagnosis medis</b>.
              </span>
            </label>

            <Button
              variant="primary"
              disabled={!consent}
              loading={busy}
              onClick={startSession}
              icon={<ArrowRight className="size-4" />}
            >
              Mulai pemeriksaan
            </Button>
          </CardBody>
        </Card>
      )}

      {step === "danger" && (
        <>
          {emergency ? (
            <UrgencyPanel level="critical" title="Segera cari pertolongan medis">
              <p>
                Gejala yang Anda tandai memerlukan penanganan segera. Jangan melanjutkan
                pemeriksaan mandiri.
              </p>
              <ul className="mt-2 list-disc space-y-0.5 pl-5">
                {[...redFlags].map((code) => (
                  <li key={code}>{RED_FLAGS.find((item) => item.code === code)?.name}</li>
                ))}
              </ul>
              <div className="mt-4 flex flex-wrap gap-2">
                <a
                  href="tel:119"
                  // Calling is the confirmation. The record is written here
                  // rather than on tick so that an accidental tick stays
                  // undoable.
                  onClick={() => void escalate(redFlags)}
                  className="bg-critical-500 inline-flex h-10 items-center gap-2 rounded-(--radius-control) px-5 text-sm font-bold text-white"
                >
                  <PhoneCall className="size-4" />
                  Hubungi 119
                </a>
                <Button
                  disabled={escalated}
                  onClick={() => {
                    setRedFlags(new Set());
                    setFatal(null);
                  }}
                >
                  Saya salah tandai
                </Button>
              </div>
            </UrgencyPanel>
          ) : (
            <Card>
              <CardHeader
                eyebrow="Langkah 2"
                title="Apakah Anda mengalami salah satu kondisi ini?"
                description="Diperiksa lebih dulu — kondisi berikut membutuhkan penanganan segera, apa pun hasil pengukuran."
              />
              <CardBody className="space-y-2.5">
                {RED_FLAGS.map((flag) => (
                  <label
                    key={flag.code}
                    className="border-hairline hover:border-critical-500/40 flex cursor-pointer items-start gap-3 rounded-xl border px-4 py-3"
                  >
                    <input
                      type="checkbox"
                      checked={redFlags.has(flag.code)}
                      onChange={(event) => {
                        const next = new Set(redFlags);
                        if (event.target.checked) next.add(flag.code);
                        else next.delete(flag.code);
                        setRedFlags(next);
                      }}
                      className="accent-critical-500 mt-0.5 size-4 shrink-0"
                    />
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold">{flag.name}</span>
                      <span className="text-content-muted block text-xs">{flag.detail}</span>
                    </span>
                  </label>
                ))}

                <div className="flex justify-between pt-2">
                  <Button icon={<ArrowLeft className="size-4" />} onClick={() => setStep("consent")}>
                    Kembali
                  </Button>
                  <Button
                    variant="primary"
                    icon={<ArrowRight className="size-4" />}
                    onClick={() => setStep("vitals")}
                  >
                    Tidak ada, lanjutkan
                  </Button>
                </div>
              </CardBody>
            </Card>
          )}
        </>
      )}

      {step === "vitals" && (
        <Card>
          <CardHeader
            eyebrow="Langkah 3"
            title="Masukkan hasil pengukuran"
            description="Isi yang Anda punya. Kosongkan yang tidak diukur — jangan menebak."
          />
          <CardBody className="space-y-5">
            <Alert tone="warning">
              Gunakan alat yang sesuai: tensimeter untuk tekanan darah, oksimeter untuk saturasi,
              termometer untuk suhu. Nilai tebakan membuat hasil screening menyesatkan.
            </Alert>

            <div className="grid gap-4 sm:grid-cols-2">
              {metrics.map((key) => (
                <Input
                  key={key}
                  label={`${METRICS[key].label} (${METRICS[key].unit})`}
                  type="number"
                  inputMode="decimal"
                  step={METRICS[key].step ?? "1"}
                  placeholder={METRICS[key].placeholder}
                  hint={METRICS[key].hint}
                  error={errors[key]}
                  value={values[key] ?? ""}
                  onChange={(event) => setValues({ ...values, [key]: event.target.value })}
                />
              ))}
            </div>

            <div className="flex justify-between">
              <Button icon={<ArrowLeft className="size-4" />} onClick={() => setStep("danger")}>
                Kembali
              </Button>
              <Button
                variant="primary"
                loading={busy}
                icon={<ArrowRight className="size-4" />}
                onClick={submitVitals}
              >
                Lanjut ke gejala
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      {step === "symptoms" && (
        <Card>
          <CardHeader
            eyebrow="Langkah 4"
            title="Gejala yang Anda rasakan"
            description="Pilih yang sesuai, lalu tentukan tingkat keparahannya."
          />
          <CardBody className="space-y-5">
            <div className="grid gap-2 sm:grid-cols-2">
              {SYMPTOMS.map((symptom) => {
                const selected = symptoms.has(symptom.code);
                return (
                  <div
                    key={symptom.code}
                    className={cn(
                      "flex items-center gap-3 rounded-xl border px-4 py-2.5 transition-colors",
                      selected ? "border-accent bg-accent/5" : "border-hairline",
                    )}
                  >
                    <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-3">
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={(event) => {
                          const next = new Map(symptoms);
                          if (event.target.checked) next.set(symptom.code, "moderate");
                          else next.delete(symptom.code);
                          setSymptoms(next);
                        }}
                        className="accent-accent size-4 shrink-0"
                      />
                      <span className="truncate text-sm font-medium">{symptom.name}</span>
                    </label>

                    {selected && (
                      <Select
                        aria-label={`Keparahan ${symptom.name}`}
                        value={symptoms.get(symptom.code)}
                        onChange={(event) => {
                          const next = new Map(symptoms);
                          next.set(symptom.code, event.target.value);
                          setSymptoms(next);
                        }}
                        className="w-28 shrink-0 py-1 text-xs"
                      >
                        {SEVERITIES.map((item) => (
                          <option key={item.value} value={item.value}>
                            {item.label}
                          </option>
                        ))}
                      </Select>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="flex items-center justify-between gap-3">
              <Button icon={<ArrowLeft className="size-4" />} onClick={() => setStep("vitals")}>
                Kembali
              </Button>
              <div className="flex items-center gap-3">
                {symptoms.size > 0 && <Badge>{symptoms.size} gejala</Badge>}
                <Button
                  variant="primary"
                  loading={busy}
                  icon={<Check className="size-4" />}
                  onClick={finish}
                >
                  Analisis hasil
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {step === "submitting" && (
        <Card>
          <CardBody className="flex flex-col items-center py-14 text-center">
            <div className="bg-accent/10 mb-4 grid size-12 place-items-center rounded-full">
              <Stethoscope className="text-accent size-5" />
            </div>
            <p className="font-display text-lg font-bold">Menganalisis hasil Anda</p>
            <p className="text-content-muted mt-1 max-w-sm text-sm">
              Aturan klinis dijalankan terlebih dahulu, lalu penjelasan disusun. Proses ini bisa
              memakan waktu hingga satu menit.
            </p>
          </CardBody>
        </Card>
      )}

      {/* The icon is the flex item; the copy stays in a single text node.
          Putting inline markup like <b> directly in a flex container turns it
          into a flex item too, which tore "119" out of the sentence. */}
      <div className="text-content-subtle mt-5 flex items-start gap-2 text-xs">
        <ShieldAlert aria-hidden className="mt-px size-3.5 shrink-0" />
        <p>
          Hasil screening ini tidak menegakkan diagnosis dan tidak dapat menyingkirkan penyakit.
          Untuk keadaan darurat hubungi <b>119</b> atau IGD terdekat.
        </p>
      </div>
    </>
  );
}

function StepIndicator({ current }: { current: Exclude<Step, "submitting"> }) {
  const order: Exclude<Step, "submitting">[] = ["consent", "danger", "vitals", "symptoms"];
  const activeIndex = order.indexOf(current);

  return (
    <ol className="mb-5 flex flex-wrap items-center gap-x-2 gap-y-1.5">
      {order.map((key, index) => {
        const done = index < activeIndex;
        const active = index === activeIndex;
        return (
          <li key={key} className="flex items-center gap-2">
            <span
              className={cn(
                "flex items-center gap-1.5 rounded-(--radius-control) px-2.5 py-1 text-xs font-semibold",
                active
                  ? "bg-accent text-accent-content"
                  : done
                    ? "bg-routine-tint text-routine-text"
                    : "bg-surface-sunken text-content-subtle",
              )}
            >
              {done ? (
                <Check aria-hidden className="size-3" />
              ) : (
                <span className="tabular">{index + 1}</span>
              )}
              {STEP_LABELS[key]}
            </span>
            {index < order.length - 1 && (
              <span aria-hidden className="bg-hairline-strong h-px w-4" />
            )}
          </li>
        );
      })}
    </ol>
  );
}
